import { useEffect, useRef, useState } from "react";
import {
  ToolContainer,
  DownloadButton,
  downloadBlob,
} from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Monitor, Square, Pause, Play, Mic, MicOff, Crop, Maximize } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { getMediaErrorMessage } from "@/lib/mediaPermissions";
import { NoticeBox } from "@/components/NoticeBox";
import { selectionToCrop, evenSize } from "./recorderRegion";
import { RecordingClock, liveSeconds, finalSeconds } from "./recorderClock";

type RecordState = "idle" | "selecting" | "recording" | "paused" | "stopped";

// Picks the best supported codec available in the current browser, in order
// of preference. Not every browser supports every combination -- this
// avoids the recorder silently failing on browsers with narrower codec support.
function pickMimeType(): string {
  // MP4 (H.264 + AAC) first where the browser can record it (Chrome/Edge 126+, Safari): it plays everywhere and
  // needs no conversion. Otherwise WebM.
  const candidates = [
    "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
    "video/mp4;codecs=avc1,mp4a.40.2",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  for (const type of candidates) {
    if (
      typeof MediaRecorder !== "undefined" &&
      MediaRecorder.isTypeSupported(type)
    ) {
      return type;
    }
  }
  return "";
}

const TOOL_INFO_CONTENT = {
  what: `Records your screen, a specific application window, or a browser tab directly from your browser, with optional microphone audio. After choosing what to share, optionally drag to select just a region of it to record, instead of the whole thing.`,
  how: `Click start recording and choose what to share (screen, window, or tab) in the browser's sharing prompt. A live preview appears — drag to select just the region you want recorded, or click "Record Full Capture" to skip that and record everything. Use the pause and stop controls as needed, then download the finished recording.`,
  tip: `Turn on the microphone toggle before you start recording if you want narration included — it can't be added retroactively to a recording that's already finished. Recording just a selected region re-draws each frame through a canvas before recording it, which uses a bit more processing than recording the raw capture directly — for very long recordings on an older machine, recording the full capture (and trimming the start and end afterward with the Trim Video tool if needed) may perform better.`,
};

/** screen-recording-2026-10-10_14-05.webm: one name per recording, so several never overwrite each other. */
const recordingName = (ext: string) => {
  const d = new Date(), z = (n: number) => String(n).padStart(2, "0");
  return `screen-recording-${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}_${z(d.getHours())}-${z(d.getMinutes())}.${ext}`;
};

export default function ScreenRecorder() {
  const [state, setState] = useState<RecordState>("idle");
  const [includeMic, setIncludeMic] = useState(true);
  const [micDevices, setMicDevices] = useState<{ id: string; label: string }[]>([]);
  const [micId, setMicId] = useState("");
  const [micLevel, setMicLevel] = useState(0);
  const meterRef = useRef<number | null>(null);
  const refreshMics = async () => {
    try {
      if (!navigator.mediaDevices || !("enumerateDevices" in navigator.mediaDevices)) return;
      const all = await navigator.mediaDevices.enumerateDevices();
      setMicDevices(all.filter(d => d.kind === "audioinput" && d.deviceId).map((d, i) => ({ id: d.deviceId, label: d.label || `Microphone ${i + 1}` })));
    } catch { /* picker stays hidden */ }
  };
  useEffect(() => { if (includeMic) refreshMics(); }, [includeMic]);
  const [includeCamera, setIncludeCamera] = useState(true);
  const [countdownOn, setCountdownOn] = useState(true);
  const [countdownLeft, setCountdownLeft] = useState(0);
  const camStreamRef = useRef<MediaStream | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [duration, setDuration] = useState(0);

  const streamRef = useRef<MediaStream | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const previewRef = useRef<HTMLVideoElement>(null);
  const selectPreviewRef = useRef<HTMLVideoElement>(null);
  const selectContainerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clockRef = useRef(new RecordingClock());
  const rafRef = useRef<number | null>(null);
  const tickerRef = useRef<Worker | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const cropVideoRef = useRef<HTMLVideoElement | null>(null);

  // Attach the shared screen to the selection preview once that <video> has mounted.
  useEffect(() => {
    if (state !== "selecting") return;
    const el = selectPreviewRef.current;
    if (el && streamRef.current) {
      el.srcObject = streamRef.current;
      el.play().catch(() => { /* autoplay is allowed for muted video; ignore */ });
    }
  }, [state]);
  // Same for the live preview while recording.
  useEffect(() => {
    if (state !== "recording" && state !== "paused") return;
    const el = previewRef.current;
    if (el && recordingStreamRef.current && el.srcObject !== recordingStreamRef.current) {
      el.srcObject = recordingStreamRef.current;
      el.play().catch(() => {});
    }
  }, [state]);

  const [selection, setSelection] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setDuration(liveSeconds(clockRef.current.elapsedMs(performance.now()))), 250);
  };
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const camPreviewRef = useRef<HTMLVideoElement>(null);
  const [camPreviewOn, setCamPreviewOn] = useState(false);
  // Turning the camera on asks for permission right away and shows a live round preview, so a blocked or
  // missing camera is noticed now, not after the recording has started.
  const toggleCamera = async () => {
    if (includeCamera) {
      camStreamRef.current?.getTracks().forEach((t) => t.stop());
      camStreamRef.current = null;
      setCamPreviewOn(false); setIncludeCamera(false);
      return;
    }
    try {
      const cam = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
      camStreamRef.current = cam;
      setIncludeCamera(true); setCamPreviewOn(true);
    } catch (err) {
      setIncludeCamera(false);
      toast.error(getMediaErrorMessage(err, "camera"), { duration: 8000 });
    }
  };
  useEffect(() => {
    if (camPreviewOn && camPreviewRef.current && camStreamRef.current) { camPreviewRef.current.srcObject = camStreamRef.current; void camPreviewRef.current.play().catch(() => {}); }
  }, [camPreviewOn, state]);

  // Leaving the page mid-recording must release the capture, worker and recorder.
  useEffect(() => () => {
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") { recorderRef.current.onstop = null; recorderRef.current.stop(); } } catch { /* already stopped */ }
    if (tickerRef.current) { tickerRef.current.postMessage("stop"); tickerRef.current = null; }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    micStreamRef.current?.getTracks().forEach(t => t.stop());
    camStreamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  const cleanupStreams = () => {
    if (meterRef.current) cancelAnimationFrame(meterRef.current);
    meterRef.current = null;
    setMicLevel(0);
    streamRef.current?.getTracks().forEach(t => t.stop());
    micStreamRef.current?.getTracks().forEach(t => t.stop());
    camStreamRef.current?.getTracks().forEach(t => t.stop());
    camStreamRef.current = null;
    setCamPreviewOn(false);
    streamRef.current = null;
    micStreamRef.current = null;
  };

  const start = async () => {
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getDisplayMedia
    ) {
      toast.error("Screen recording isn't supported in this browser");
      return;
    }
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 } as MediaTrackConstraints,
        audio: true, // system audio, where the browser/OS supports sharing it
      });
      streamRef.current = displayStream;
      setSelection(null);

      // If the user stops sharing via the browser's own "Stop sharing" UI
      // at any point (during selection or while recording), treat that
      // the same as pressing Stop.
      displayStream.getVideoTracks()[0].onended = () => {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
          stop();
        } else {
          cleanupStreams();
          setState("idle");
        }
      };

      // The preview <video> only exists once the page is in the "selecting" state, so the stream is attached
      // in the effect below, after it has mounted (attaching it here left the preview black).
      setState("selecting");
    } catch (err) {
      // User cancelled the share picker, or permission denied
      toast.error(
        "Screen recording wasn't started (permission denied or cancelled)"
      );
    }
  };

  const getRelativePosAt = (clientX: number, clientY: number) => {
    const rect = selectContainerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: Math.max(0, Math.min(rect.width, clientX - rect.left)),
      y: Math.max(0, Math.min(rect.height, clientY - rect.top)),
    };
  };
  // Thin wrapper kept so the existing mouse-event call sites below don't
  // need to change -- getRelativePosAt is the one real implementation
  // both mouse and touch paths now share.
  const getRelativePos = (e: React.MouseEvent) => getRelativePosAt(e.clientX, e.clientY);
  const onMouseDown = (e: React.MouseEvent) => {
    const pos = getRelativePos(e);
    setDragStart(pos);
    setSelection({ x: pos.x, y: pos.y, w: 0, h: 0 });
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragStart) return;
    const pos = getRelativePos(e);
    setSelection({
      x: Math.min(dragStart.x, pos.x),
      y: Math.min(dragStart.y, pos.y),
      w: Math.abs(pos.x - dragStart.x),
      h: Math.abs(pos.y - dragStart.y),
    });
  };
  const onMouseUp = () => setDragStart(null);

  // Real touch support -- the same confirmed bug found and fixed across
  // every other drag-based selection tool in the site tonight: this
  // region-selection area only ever had mouse events, which touch
  // interactions don't reliably fire for an actual drag/swipe gesture.
  // e.preventDefault() on touchmove stops the page scrolling out from
  // under the drag.
  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 0) return;
    const t = e.touches[0];
    const pos = getRelativePosAt(t.clientX, t.clientY);
    setDragStart(pos);
    setSelection({ x: pos.x, y: pos.y, w: 0, h: 0 });
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!dragStart || e.touches.length === 0) return;
    e.preventDefault();
    const t = e.touches[0];
    const pos = getRelativePosAt(t.clientX, t.clientY);
    setSelection({
      x: Math.min(dragStart.x, pos.x),
      y: Math.min(dragStart.y, pos.y),
      w: Math.abs(pos.x - dragStart.x),
      h: Math.abs(pos.y - dragStart.y),
    });
  };

  const beginRecording = async (useRegion: boolean) => {
    const displayStream = streamRef.current;
    if (!displayStream) return;

    try {
      let combinedAudioDest: MediaStreamAudioDestinationNode | null = null;
      let audioCtx: AudioContext | null = null;
      if (includeMic) {
        try {
          const micStream = await navigator.mediaDevices.getUserMedia({ audio: micId ? { deviceId: { exact: micId } } : true });
          micStreamRef.current = micStream;
          refreshMics();
          audioCtx = new AudioContext();
          combinedAudioDest = audioCtx.createMediaStreamDestination();
          if (displayStream.getAudioTracks().length > 0) {
            audioCtx.createMediaStreamSource(displayStream).connect(combinedAudioDest);
          }
          const micSource = audioCtx.createMediaStreamSource(micStream);
          micSource.connect(combinedAudioDest);
          // Live level meter so a dead or wrong microphone is noticed while recording, not afterwards.
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 512;
          micSource.connect(analyser);
          const buf = new Uint8Array(analyser.fftSize);
          const tick = () => {
            analyser.getByteTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) sum += ((buf[i] - 128) / 128) ** 2;
            setMicLevel(Math.min(1, Math.sqrt(sum / buf.length) * 4));
            meterRef.current = requestAnimationFrame(tick);
          };
          tick();
        } catch (err) {
          toast.error(`${getMediaErrorMessage(err, "microphone")} Recording video + system audio only.`, { duration: 8000 });
        }
      }

      let recordingStream: MediaStream;

      const regionOk = !!(useRegion && selection && selection.w >= 20 && selection.h >= 20 && selectPreviewRef.current && selectContainerRef.current);

      // Optional round camera bubble in the bottom-right corner of the recording.
      let camVideo: HTMLVideoElement | null = null;
      if (includeCamera) {
        try {
          const live = camStreamRef.current && camStreamRef.current.getVideoTracks().some((t) => t.readyState === "live") ? camStreamRef.current : null;
          const cam = live ?? await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
          camStreamRef.current = cam;
          camVideo = document.createElement("video");
          camVideo.muted = true; camVideo.playsInline = true; camVideo.srcObject = cam;
          await camVideo.play().catch(() => {});
          for (let i = 0; i < 20 && !camVideo.videoWidth; i++) await new Promise((r) => setTimeout(r, 100));
          if (!camVideo.videoWidth) { toast.error("The camera is on but sent no picture. Recording the screen without it.", { duration: 8000 }); camVideo = null; }
        } catch (err) {
          toast.error(`${getMediaErrorMessage(err, "camera")} Recording the screen without the camera.`, { duration: 8000 });
        }
      }

      if (countdownOn) {
        for (let n = 3; n > 0; n--) { setCountdownLeft(n); await new Promise((r) => setTimeout(r, 1000)); }
        setCountdownLeft(0);
      }

      if (regionOk || camVideo) {
        // Records the selected region only, by continuously re-drawing
        // just that crop of each incoming video frame onto an offscreen
        // canvas, then recording *that canvas's own stream* instead of
        // the raw display stream -- MediaRecorder has no built-in way to
        // crop a stream, so the crop has to happen frame-by-frame before
        // recording starts, not after.
        const preview = selectPreviewRef.current;
        const even = evenSize;
        let cropX = 0, cropY = 0, cropW = even(preview?.videoWidth || displayStream.getVideoTracks()[0].getSettings().width || 1280), cropH = even(preview?.videoHeight || displayStream.getVideoTracks()[0].getSettings().height || 720);
        if (regionOk && preview && selectContainerRef.current && selection) {
          const c = selectionToCrop(
            selection,
            { w: selectContainerRef.current.clientWidth, h: selectContainerRef.current.clientHeight },
            { w: preview.videoWidth, h: preview.videoHeight },
          );
          cropX = c.x; cropY = c.y; cropW = c.w; cropH = c.h;
        }

        // The selection preview unmounts when recording starts, so frames are read from a private video element.
        const video = document.createElement("video");
        video.muted = true;
        video.playsInline = true;
        video.srcObject = displayStream;
        await video.play().catch(() => {});
        cropVideoRef.current = video;

        const canvas = document.createElement("canvas");
        // Output equals the crop in source pixels (even for the encoder); very large captures are capped.
        const capScale = Math.min(1, 3840 / cropW, 2160 / cropH);
        canvas.width = evenSize(cropW * capScale);
        canvas.height = evenSize(cropH * capScale);
        const ctx = canvas.getContext("2d")!;

        const drawFrame = () => {
          ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, canvas.width, canvas.height);
          if (camVideo && camVideo.videoWidth) {
            const d = Math.round(Math.min(canvas.width, canvas.height) * 0.24), m = Math.round(d * 0.12);
            const cx = canvas.width - m - d / 2, cy = canvas.height - m - d / 2;
            const side = Math.min(camVideo.videoWidth, camVideo.videoHeight);
            ctx.save();
            ctx.beginPath(); ctx.arc(cx, cy, d / 2, 0, Math.PI * 2); ctx.clip();
            ctx.drawImage(camVideo, (camVideo.videoWidth - side) / 2, (camVideo.videoHeight - side) / 2, side, side, cx - d / 2, cy - d / 2, d, d);
            ctx.restore();
            ctx.beginPath(); ctx.arc(cx, cy, d / 2, 0, Math.PI * 2); ctx.lineWidth = Math.max(2, d * 0.03); ctx.strokeStyle = "#fff"; ctx.stroke();
          }
        };
        drawFrame();
        // A page timer is throttled to about 1 frame a second as soon as you switch to the window you are
        // recording (this tab goes to the background), which froze region recordings. A worker's timer is not
        // throttled, so it drives the frame copying.
        try {
          const src = "let t=setInterval(()=>postMessage(0),33);onmessage=()=>{clearInterval(t);close()}";
          const w = new Worker(URL.createObjectURL(new Blob([src], { type: "text/javascript" })));
          w.onmessage = drawFrame;
          tickerRef.current = w;
        } catch {
          const loop = () => { drawFrame(); rafRef.current = requestAnimationFrame(loop); };
          loop();
        }

        const canvasStream = canvas.captureStream(30);
        const audioTracks = combinedAudioDest
          ? combinedAudioDest.stream.getAudioTracks()
          : displayStream.getAudioTracks();
        recordingStream = new MediaStream([...canvasStream.getVideoTracks(), ...audioTracks]);
      } else {
        const audioTracks = combinedAudioDest
          ? combinedAudioDest.stream.getAudioTracks()
          : displayStream.getAudioTracks();
        recordingStream = new MediaStream([...displayStream.getVideoTracks(), ...audioTracks]);
      }

      recordingStreamRef.current = recordingStream;
      if (previewRef.current) previewRef.current.srcObject = recordingStream;

      const mimeType = pickMimeType();
      chunksRef.current = [];
      const recorder = new MediaRecorder(recordingStream, mimeType ? { mimeType } : undefined);
      recorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || "video/webm" });
        setRecordedBlob(blob);
        setRecordedUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(blob); });
        chunksRef.current = [];
        if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        if (tickerRef.current) { tickerRef.current.postMessage("stop"); tickerRef.current = null; }
        if (cropVideoRef.current) { cropVideoRef.current.srcObject = null; cropVideoRef.current = null; }
        recordingStreamRef.current = null;
        if (audioCtx) { void audioCtx.close().catch(() => {}); }
        cleanupStreams();
        stopTimer();
      };

      // The timer starts when the recorder really starts and is read from the clock, not counted in ticks (B-0174).
      recorder.onstart = () => { clockRef.current.start(performance.now()); startTimer(); };
      recorder.onpause = () => clockRef.current.pause(performance.now());
      recorder.onresume = () => clockRef.current.resume(performance.now());
      recorderRef.current = recorder;
      clockRef.current.reset();
      recorder.start();
      setState("recording");
      setDuration(0);
    } catch (err) {
      console.error("Failed to start recording:", err);
      toast.error("Couldn't start recording. Try again.");
      cleanupStreams();
      setState("idle");
    }
  };


  const pause = () => {
    recorderRef.current?.pause();
    clockRef.current.pause(performance.now());
    setState("paused");
    stopTimer();
    setDuration(liveSeconds(clockRef.current.elapsedMs(performance.now())));
  };

  const resume = () => {
    recorderRef.current?.resume();
    clockRef.current.resume(performance.now());
    setState("recording");
    startTimer();
  };

  const stop = () => {
    // Final length from the clock at the moment of Stop, so the timer, the "Recorded" label and the file agree.
    const rec = recorderRef.current;
    // A second Stop (button plus the browser's own "Stop sharing") must not throw on an already-stopped recorder.
    if (!rec || rec.state === "inactive") return;
    const finalMs = clockRef.current.stop(performance.now());
    stopTimer();
    setDuration(finalSeconds(finalMs));
    // Stop the frame-copy loop first so nothing draws into the canvas while the recorder flushes.
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (tickerRef.current) { tickerRef.current.postMessage("stop"); tickerRef.current = null; }
    // The live preview must let go of the stream before it is torn down.
    if (previewRef.current) previewRef.current.srcObject = null;
    try { rec.stop(); } catch (err) { console.error("Recorder stop failed:", err); cleanupStreams(); }
    setState("stopped");
  };

  const reset = () => {
    setState("idle");
    setRecordedUrl(null);
    setRecordedBlob(null);
    clockRef.current.reset();
    setDuration(0);
    setSelection(null);
  };

  const download = () => {
    if (!recordedBlob) return;
    const ext = recordedBlob.type.includes("mp4") ? "mp4" : "webm";
    downloadBlob(recordedBlob, recordingName(ext));
  };

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60)
      .toString()
      .padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <ToolContainer>
      {state === "idle" && typeof navigator !== "undefined" && !navigator.mediaDevices?.getDisplayMedia && (
        <NoticeBox className="mb-4 text-sm">
          Screen recording works in desktop browsers (Chrome, Edge, Firefox, Safari on a computer). Phone browsers don't allow it — on your phone, use the built-in screen recorder (swipe down for Control Centre on iPhone, or Quick Settings on Android), then bring the video back here to trim, compress or convert it.
        </NoticeBox>
      )}
      {state === "idle" && (
        <div className="text-center py-12 border-2 border-dashed border-border rounded-xl">
          <Monitor className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">
            Record your screen, a window, or a browser tab — entirely in your
            browser. Nothing is uploaded anywhere.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
            <button
              onClick={toggleCamera}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${includeCamera ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}
              aria-pressed={includeCamera}
            >
              Camera bubble {includeCamera ? "on" : "off"}
            </button>
            <button
              onClick={() => setCountdownOn(!countdownOn)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${countdownOn ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}
              aria-pressed={countdownOn}
            >
              3-2-1 countdown {countdownOn ? "on" : "off"}
            </button>
            <button
              onClick={() => setIncludeMic(!includeMic)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                includeMic
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground"
              }`}
            >
              {includeMic ? (
                <Mic className="h-3.5 w-3.5" />
              ) : (
                <MicOff className="h-3.5 w-3.5" />
              )}
              {includeMic ? "Microphone on" : "Microphone off"}
            </button>
          </div>
          {includeMic && micDevices.length > 1 && (
            <div className="flex items-center justify-center gap-2 mb-4 text-sm">
              <label htmlFor="mic-select">Microphone</label>
              <select id="mic-select" value={micId} onChange={e => setMicId(e.target.value)} className="border rounded px-2 py-1 bg-background max-w-[260px]">
                <option value="">Default</option>
                {micDevices.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
            </div>
          )}
          {includeCamera && camPreviewOn && (
            <div className="flex flex-col items-center gap-1 mb-4">
              <video ref={camPreviewRef} muted playsInline autoPlay className="h-24 w-24 rounded-full object-cover border-2 border-white shadow" style={{ transform: "scaleX(-1)" }} />
              <p className="text-xs text-muted-foreground">This round picture will sit in the bottom-right corner of your recording.</p>
            </div>
          )}
          <Button onClick={start} size="lg">
            <Monitor className="h-4 w-4 mr-2" /> Start Recording
          </Button>
        </div>
      )}

      {countdownLeft > 0 && (
        <div role="status" aria-live="assertive" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 text-white">
          <span className="text-9xl font-bold" data-testid="countdown">{countdownLeft}</span>
        </div>
      )}

      {state === "selecting" && (
        <div className="space-y-4">
          <div
            ref={selectContainerRef}
            className="relative w-full rounded-lg border bg-black max-h-[400px] overflow-hidden cursor-crosshair select-none"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onMouseUp}
            onTouchCancel={onMouseUp}
            style={{ touchAction: "none" }}
          >
            <video
              ref={selectPreviewRef}
              autoPlay
              muted
              className="w-full max-h-[400px] pointer-events-none"
            />
            {selection && selection.w >= 4 && selection.h >= 4 && (
              <div
                className="absolute border-2 border-primary bg-primary/10 pointer-events-none"
                style={{ left: selection.x, top: selection.y, width: selection.w, height: selection.h }}
              />
            )}
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Drag across the preview above to select just the region you want to record, or record
            the full capture without selecting anything.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            {selection && selection.w >= 20 && selection.h >= 20 && (
              <Button onClick={() => beginRecording(true)} className="gap-2">
                <Crop className="h-4 w-4" /> Record Selected Region
              </Button>
            )}
            <Button variant="outline" onClick={() => beginRecording(false)} className="gap-2">
              <Maximize className="h-4 w-4" /> Record Full Capture
            </Button>
            <Button variant="ghost" onClick={() => { cleanupStreams(); setState("idle"); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {(state === "recording" || state === "paused") && (
        <div className="space-y-4">
          <video
            ref={previewRef}
            autoPlay
            muted
            className="w-full rounded-lg border bg-black max-h-[400px]"
          />
          <div className="flex items-center justify-center gap-4">
            <Card className="px-4 py-2 flex items-center gap-2">
              <span
                className={`w-2.5 h-2.5 rounded-full ${state === "recording" ? "bg-red-500 animate-pulse" : "bg-amber-500"}`}
              />
              <span className="font-mono font-medium">
                {formatTime(duration)}
              </span>
            </Card>
            {includeMic && micStreamRef.current && (
              <div className="flex items-center gap-2" title="Microphone level">
                <Mic className="h-3.5 w-3.5" />
                <div className="h-2 w-24 rounded bg-muted overflow-hidden" role="meter" aria-label="Microphone level" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(micLevel * 100)} data-testid="mic-meter">
                  <div className="h-full bg-green-500 transition-[width] duration-75" style={{ width: `${micLevel * 100}%` }} />
                </div>
              </div>
            )}
            {duration >= 1800 && <span className="text-xs text-amber-600">Long recording: stop and save soon so the file stays manageable.</span>}
            {state === "recording" ? (
              <Button variant="outline" onClick={pause}>
                <Pause className="h-4 w-4 mr-2" /> Pause
              </Button>
            ) : (
              <Button variant="outline" onClick={resume}>
                <Play className="h-4 w-4 mr-2" /> Resume
              </Button>
            )}
            <Button variant="destructive" onClick={stop}>
              <Square className="h-4 w-4 mr-2" /> Stop
            </Button>
          </div>
        </div>
      )}

      {state === "stopped" && recordedUrl && (
        <div className="space-y-4">
          <video
            src={recordedUrl}
            controls
            className="w-full rounded-lg border max-h-[400px]"
          />
          <div className="flex items-center justify-center gap-3">
            <DownloadButton blob={recordedBlob} fileName={recordingName(recordedBlob?.type.includes("mp4") ? "mp4" : "webm")} label="Download Recording" />
            <Button variant="outline" onClick={reset}>
              Record Another
            </Button>
          </div>
          {recordedBlob && recordedBlob.size > 200 * 1024 * 1024 && (
            <NoticeBox className="text-xs text-center">This recording is {(recordedBlob.size / 1024 / 1024).toFixed(0)} MB. Compress it before sharing.</NoticeBox>
          )}
          <p className="text-xs text-center text-muted-foreground">
            Next: <a className="underline" href="/tool/trim-video">Trim it</a> · <a className="underline" href="/tool/video-to-gif">Make a GIF</a> · <a className="underline" href="/tool/compress-video">Make it smaller</a> (download first, then open the tool)
          </p>
          <p className="text-xs text-muted-foreground text-center">
            Recorded {formatTime(duration)} — downloads as{" "}
            {recordedBlob?.type.includes("mp4")
              ? "MP4, which plays almost everywhere."
              : "WebM, the format your browser recorded in. WebM plays in all modern browsers; use Convert Video if you need an MP4 for older software."}
          </p>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
